<template>
  <div class="selection-group" :class="feelingClass">
    <label v-if="label" class="selection-group__label" :for="name">
      {{ label }}
    </label>
    <ul
      v-if="type === 'checkbox' || type === 'radio'"
      class="selection-group__list"
      :id="`${_uid}-${name}`"
    >
      <li
        class="selection-group__list-item"
        v-for="option in options"
        :key="`${_uid}-${option.id}`"
      >
        <input
          class="selection-group__list-input"
          :type="type"
          :id="`${_uid}-${option.id}`"
          :value="option.id"
          :name="`${_uid}-${name}`"
          :checked="checked.includes(option.id)"
          :disabled="disabled.includes(option.id)"
          @change="$emit('change', checkedLocal)"
          v-model="checkedLocal"
        >
        <label
          class="selection-group__list-label"
          v-bind:class="{ 'selection-group__list-label--flat': option.flat || false }"
          :for="`${_uid}-${option.id}`"
        >
          <span v-html="option.label" />
          <div
            v-if="option.labelSecondary"
            class="selection-group__list-secondary-label"
            v-html="option.labelSecondary"
          />
        </label>
      </li>
    </ul>
    <select
      v-else
      class="selection-group__select"
      v-model="checkedLocal"
      :id="`${_uid}-${name}`"
      @change="$emit('change', checkedLocal)"
      :multiple="type === 'multiple-select'"
    >
      <option
        v-for="option in options"
        :key="`${_uid}-${option.id}`"
        class="selection-group__select-option"
        :id="`${_uid}-${option.id}`"
        :value="option.id"
        v-html="option.label"
      />
    </select>
    <span v-if="helper" class="selection-group__helper">{{ helper }}</span>
  </div>
</template>

<script>
export default {
  name: 'SelectionGroup',
  model: {
    prop: 'checked',
    event: 'change',
  },
  data() {
    return {
      checkedLocal: [],
    };
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (prop) => [
        'checkbox',
        'radio',
        'select',
        'multiple-select',
      ].includes(prop),
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
    },
    helper: {
      type: String,
    },
    options: {
      type: Array,
      required: true,
      // add validator
    },
    checked: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    disabled: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    feeling: {
      type: String,
      validator: (prop) => [
        'positive',
        'highligted',
        'pending',
        'muted',
        'negative',
      ].includes(prop),
    },
  },
  computed: {
    feelingClass() {
      return this.feeling ? `text-field--${this.feeling}` : false;
    },
  },
  watch: {
    checked() {
      this.checkedLocal = this.checked;
    },
    checkedLocal(newValue, oldValue) {
      if (Array.isArray(newValue)) {
        const checked = newValue.filter((item) => !oldValue.includes(item));
        const unchecked = oldValue.filter((item) => !newValue.includes(item));
        if (checked.length > 0) {
          this.$emit('checked', checked[0]);
        } else {
          this.$emit('unchecked', unchecked[0]);
        }
      }
    },
  },
};
</script>

<style scoped>
.selection-group {
  display: flex;
  flex-direction: column;
}

.selection-group__label {
  margin-bottom: var(--unit-extra-small);
  margin-left: calc(2 * var(--unit-extra-small));
  font-weight: bold;
}

.selection-group__list {
  margin: 0;
  padding-left: 0;
}

.selection-group__list * + * {
  margin-top: 0.1rem;
}

.selection-group__list-item {
  display: flex;
  align-items: center;
  list-style-type: none;
}

.selection-group__list-input {
  margin-right: var(--unit-tiny);
}

.selection-group__list-label {
  display: flex;
  flex-direction: column;
}

.selection-group__list-label--flat {
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.selection-group__list-secondary-label {
  font-size: 0.8rem;
}

.selection-group__select {
  width: 16rem;
  display: inline-block;
  padding: var(--unit-extra-small);
  padding-right: calc(2 * var(--unit-extra-small));
  border-radius: var(--token-border-radius-small);
  border: var(--unit-extra-small) solid transparent;
  background-color: var(--token-color-fill-light);
  outline: none;
}

.selection-group__select:hover {
  box-shadow: var(--token-glow);
}

.selection-group__select:focus {
  background-color: var(--token-color-fill-highlighted);
  box-shadow: var(--token-glow-highlighted);
}

.selection-group__helper {
  margin-top: var(--unit-extra-small);
  margin-left: calc(2 * var(--unit-extra-small));
  font-size: var(--token-body-size-small);
  line-height: var(--token-body-line-small);
}

.selection-group--positive .selection-group__helper {
  color: var(--token-color-body-positive);
}

.selection-group--negative .selection-group__helper {
  color: var(--token-color-body-negative);
}
</style>
